import { useLocation, Link, Navigate } from "react-router-dom";
import Seo from "../components/Seo";
import Breadcrumbs, { breadcrumbJsonLd } from "../components/Breadcrumbs";
import { findCity } from "../data/cities";
import { PHONE_DISPLAY, PHONE_TEL } from "../components/Layout";
import { siteUrl } from "../lib/siteUrl";
import PageFinalCta from "../components/sections/PageFinalCta";
import { getCityVoiceFaq } from "../data/voice-faq";
import { VoiceFaqSection } from "../components/home/HomeSections";

export const REPARATION_PARIS_PATHS = Array.from({ length: 20 }, (_, i) => `/reparation-electromenager-paris-${i + 1}`);

const APPLIANCES = [
    { label: "Lave-linge", icon: "fa-shirt", href: (n) => `/reparateur-lave-linge-paris-${n}`, panne: "fuite, essorage, code erreur" },
    { label: "Lave-vaisselle", icon: "fa-sink", href: (n) => `/reparateur-lave-vaisselle-paris-${n}`, panne: "vaisselle sale, vidange, bras bloqué" },
    { label: "Réfrigérateur", icon: "fa-temperature-low", href: (n) => `/reparateur-frigo-paris-${n}`, panne: "ne refroidit plus, givre, bruit" },
    { label: "Four", icon: "fa-fire-burner", href: (n) => `/reparation-four-paris-${n}`, panne: "ne chauffe plus, porte, pyrolyse" },
    { label: "Sèche-linge", icon: "fa-wind", href: (n) => `/reparation-seche-linge-paris-${n}`, panne: "ne sèche plus, résistance, tambour" },
];

export default function ReparationParisPage() {
    const location = useLocation();
    const n = parseInt(location.pathname.replace("/reparation-electromenager-paris-", ""), 10);
    const city = findCity(`paris-${n}`);
    if (!city) return <Navigate to="/reparateur-electromenager-paris" replace />;

    const suffix = n === 1 ? "1er" : `${n}ᵉ`;
    const url = siteUrl(location.pathname);
    const crumbs = [
        { name: "Accueil", to: "/" },
        { name: "Réparateur Paris", to: "/reparateur-electromenager-paris" },
        { name: `Réparation ${suffix}` },
    ];
    const voiceFaq = getCityVoiceFaq(city);

    const jsonLd = [
        breadcrumbJsonLd(crumbs, url),
        {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": `Réparation électroménager Paris ${suffix}`,
            "serviceType": "Réparation d'appareils électroménagers à domicile",
            "areaServed": { "@type": "Place", "name": `Paris ${suffix} (${city.postal})` },
            "provider": { "@type": "LocalBusiness", "name": "Electrocase", "telephone": PHONE_DISPLAY, "url": siteUrl("/") },
            "url": url,
        },
        {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": voiceFaq.map((f) => ({
                "@type": "Question",
                "name": f.q,
                "acceptedAnswer": { "@type": "Answer", "text": f.a },
            })),
        },
    ];

    return (
        <>
            <Seo
                title={`Réparation électroménager Paris ${suffix} (${city.postal}) – 24-48 h | Electrocase`}
                description={`Réparation électroménager dans le ${suffix} arrondissement de Paris : lave-linge, frigo, lave-vaisselle, four, sèche-linge. Devis gratuit, garantie 18 mois. ☎ ${PHONE_DISPLAY}`}
                keywords={`réparation électroménager paris ${n}, réparation électroménager ${city.postal}, dépannage électroménager paris ${n}ᵉ arrondissement`}
                canonical={url}
                jsonLd={jsonLd}
            />

            <section className="bg-[var(--paper)] border-b border-[var(--line)]">
                <div className="max-w-7xl mx-auto px-5 lg:px-10 py-12 lg:py-16 grid lg:grid-cols-2 gap-10 items-center">
                    <div>
                    <Breadcrumbs items={crumbs} />
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--amber)] mt-6">Paris {suffix} · {city.postal}</p>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-[var(--navy)] mt-3 leading-tight" data-testid="reparation-paris-h1">
                        Réparation électroménager Paris&nbsp;{suffix}
                    </h1>
                    <p className="text-base text-[var(--ink-soft)] mt-5 max-w-3xl leading-relaxed">
                        {city.intro} Nos techniciens assurent la réparation de votre électroménager à domicile dans tout le {suffix} arrondissement
                        ({city.neighborhoods.slice(0, 4).join(", ")}) sous 24 à 48&nbsp;heures, avec devis gratuit et garantie 18&nbsp;mois.
                    </p>
                    <div className="flex flex-wrap gap-4 mt-8">
                        <a href={PHONE_TEL} data-testid="reparation-paris-call-btn" className="inline-flex items-center gap-2 bg-[var(--amber)] text-[var(--navy)] font-black px-7 py-3.5 rounded-full hover:brightness-105 transition">
                            <i className="fa-solid fa-phone" /> {PHONE_DISPLAY}
                        </a>
                        <Link to={`/reparateur-electromenager-paris-${n}`} data-testid="reparation-paris-hub-link" className="inline-flex items-center gap-2 border-2 border-[var(--navy)] text-[var(--navy)] font-bold px-7 py-3.5 rounded-full hover:bg-[var(--navy)] hover:text-white transition">
                            Réparateur Paris {suffix} <i className="fa-solid fa-arrow-right text-sm" />
                        </Link>
                    </div>
                    </div>
                    <picture>
                        <source srcSet={`/reparation-paris/paris-${n}.webp`} type="image/webp" />
                        <img
                            src={`/reparation-paris/paris-${n}.jpg`}
                            alt={`Technicien Electrocase réparant un appareil électroménager à Paris ${suffix}, près de ${city.landmarks[0]}`}
                            width="1000"
                            height="671"
                            data-testid="reparation-paris-hero-img"
                            className="rounded-3xl shadow-xl border border-[var(--line)] w-full h-auto object-cover"
                        />
                    </picture>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-5 lg:px-10 py-14">
                <h2 className="text-lg md:text-lg font-black text-[var(--navy)]">Quel appareil réparer dans le {suffix}&nbsp;?</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-6">
                    {APPLIANCES.map((a) => (
                        <Link key={a.label} to={a.href(n)} data-testid={`reparation-paris-appliance-${a.label.toLowerCase().replace(/[^a-z]/g, "")}`} className="group border border-[var(--line)] rounded-2xl p-5 bg-white hover:border-[var(--amber)] hover:shadow-lg transition">
                            <i className={`fa-solid ${a.icon} text-2xl text-[var(--amber)]`} />
                            <h3 className="font-black text-[var(--navy)] mt-3">{a.label}</h3>
                            <p className="text-sm text-[var(--ink-soft)] mt-1">{a.panne}</p>
                            <span className="text-sm font-bold text-[var(--navy)] mt-3 inline-block group-hover:text-[var(--amber)] transition">Réparer à Paris {n} →</span>
                        </Link>
                    ))}
                </div>
                <div className="mt-10 border border-[var(--line)] rounded-2xl p-6 bg-[var(--paper)]">
                    <h2 className="text-lg md:text-lg font-black text-[var(--navy)]">Zone couverte dans le {suffix} arrondissement</h2>
                    <p className="text-sm text-[var(--ink-soft)] mt-3 leading-relaxed">
                        Quartiers desservis : {city.neighborhoods.join(", ")}. Repères : {city.landmarks.join(", ")}. Accès : {city.access}.
                        Habitat typique : {city.habitat}.
                    </p>
                </div>
            </section>

            <VoiceFaqSection items={voiceFaq} />

            {/* ARRONDISSEMENTS VOISINS — maillage interne */}
            <section className="max-w-7xl mx-auto px-5 lg:px-10 pb-14" data-testid="reparation-paris-voisins">
                <h2 className="text-lg md:text-lg font-black text-[var(--navy)]">Réparation dans les arrondissements voisins</h2>
                <div className="flex flex-wrap gap-3 mt-4">
                    {[n === 1 ? 20 : n - 1, n === 20 ? 1 : n + 1].map((v) => (
                        <Link key={v} to={`/reparation-electromenager-paris-${v}`} data-testid={`voisin-paris-${v}`} className="inline-flex items-center gap-2 border border-[var(--line)] rounded-full px-5 py-2.5 text-sm font-bold text-[var(--navy)] hover:border-[var(--amber)] hover:text-[var(--amber)] transition">
                            <i className="fa-solid fa-location-dot text-[var(--amber)]" /> Réparation Paris {v === 1 ? "1er" : `${v}ᵉ`}
                        </Link>
                    ))}
                    <Link to="/reparateur-electromenager-paris" className="inline-flex items-center gap-2 border border-[var(--line)] rounded-full px-5 py-2.5 text-sm font-bold text-[var(--navy)] hover:border-[var(--amber)] hover:text-[var(--amber)] transition">
                        <i className="fa-solid fa-city text-[var(--amber)]" /> Tout Paris
                    </Link>
                    <Link to="/carte-interventions-idf" className="inline-flex items-center gap-2 border border-[var(--line)] rounded-full px-5 py-2.5 text-sm font-bold text-[var(--navy)] hover:border-[var(--amber)] hover:text-[var(--amber)] transition">
                        <i className="fa-solid fa-map-location-dot text-[var(--amber)]" /> Carte des interventions
                    </Link>
                </div>
            </section>

            <PageFinalCta
                eyebrow={`Réparation électroménager dans le ${suffix} ?`}
                title="Appelez maintenant, intervention sous 24 à 48 h."
                subtitle="Devis gratuit par téléphone, Bonus Réparation appliqué, garantie 18 mois."
                secondaryHref="/contact"
            />
        </>
    );
}
